// The phone in this browser (event-calls S3a, S3b): asks our server for a short-lived token for this person and browser, then
// connects the calling library (src/lib/phone-library.ts) with it. The library is loaded only when someone turns the phone on, so nobody
// else downloads it. A token about to expire is swapped for a fresh one without a word to the person; a closed connection
// reads as "couldn't connect" with Try again. A call that rings it is handed to `onCall`. Tests swap the library for `window.__phoneStub` (dev and test builds only).
import { postApi } from '@/lib/api';
import { findFailure, noteFailure } from '@/lib/failures';
import type { PhoneCall, PhoneClient } from '@/lib/phone-library';

export type { PhoneCall };

const TOKEN_API = '/api/voice/token';
const KEY = 'rmp2.phone.device';
const TOKEN_EXPIRING = 34001;
export const LOST = 'This browser lost its phone connection — try again.';

export type PhoneState = 'off' | 'connecting' | 'connected' | 'failed';
type Make = (token: string) => PhoneClient | Promise<PhoneClient>;
declare global {
  interface Window { __phoneStub?: Make }
}

/** One random key per browser, kept on this device, so a person's laptop and phone are two separate phones. */
export function deviceKey(): string {
  try {
    const kept = localStorage.getItem(KEY);
    if (kept && /^[A-Za-z0-9_-]{8,64}$/.test(kept)) return kept;
  } catch { /* storage blocked: a fresh key per page */ }
  const made = typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, '0')).join('');
  try { localStorage.setItem(KEY, made); } catch { /* not kept */ }
  return made;
}

async function token(): Promise<{ token: string | null; message: string; retry: boolean }> {
  const r = await postApi<{ token?: unknown }>(TOKEN_API, { device_key: deviceKey() });
  const t = r.ok && typeof r.data?.token === 'string' ? r.data.token : null;
  // our server out of reach (noted as a network failure) may come back; a refusal will not
  return { token: t, message: t ? '' : r.message || LOST, retry: !r.ok && findFailure(r.message)?.reason === 'network' };
}

async function make(t: string): Promise<PhoneClient> {
  if ((import.meta.env.DEV || import.meta.env.MODE === 'test') && window.__phoneStub) return window.__phoneStub(t);
  return (await import('@/lib/phone-library')).phoneClient(t);
}

export interface Phone { stop(): void }

/** Connect this browser. `onState` hears every change; `failed` carries the sentence to show, and whether the connection
 * (not our server's answer) failed, so trying again later may work. */
export function startPhone(onState: (s: PhoneState, message?: string, retry?: boolean) => void, onCall?: (call: PhoneCall) => void): Phone {
  let stopped = false;
  let client: PhoneClient | null = null;
  const drop = () => {
    const c = client;
    client = null;
    try { c?.disconnect(); } catch { /* already down */ }
  };
  // `why` travels with a report only (load, error, closed); a refused token was already noted by the request
  const fail = (message: string, why?: string, retry = Boolean(why)) => {
    if (stopped) return;
    drop();
    if (why) noteFailure({ reason: 'server', message, code: 'phone-connection', details: why });
    onState('failed', message, retry);
  };
  const open = async () => {
    const t = await token();
    if (stopped) return;
    if (!t.token) return fail(t.message, undefined, t.retry);
    let c: PhoneClient;
    try {
      c = await make(t.token);
    } catch {
      return fail(LOST, 'load');
    }
    if (stopped) { try { c.disconnect(); } catch { /* not up */ } return; }
    const mine = () => client === c && !stopped;
    c.on('ready', () => { if (mine()) onState('connected'); });
    c.on('error', (code) => {
      if (!mine()) return;
      // a token near its end: swap in a fresh one rather than show an error
      if (code === TOKEN_EXPIRING) { drop(); void open(); return; }
      fail(LOST, `error ${code ?? ''}`.trim());
    });
    c.on('closed', () => { if (mine()) fail(LOST, 'closed'); });
    c.onCall((call) => { if (mine()) onCall?.(call); else call.hangup(); });
    client = c;
    try {
      c.connect();
    } catch {
      fail(LOST, 'connect');
    }
  };
  onState('connecting');
  void open();
  return { stop: () => { stopped = true; drop(); } };
}

/** Turn this browser's phone off for good: the server deletes what it made for it. */
export async function leavePhone(): Promise<{ ok: boolean; message: string }> {
  const r = await postApi<{ left: boolean }>(TOKEN_API, { device_key: deviceKey(), leave: true });
  return { ok: r.ok, message: r.message };
}
